import React, { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Pagination } from "../components/common/Pagination";
import { CashInOutRecord, CashRecord } from "../api/services/cashExchange/type";
import { CashRecordsFilters } from "../components/cashExchange/CashRecordsFilters";
import { CashRecordsTable } from "../components/cashExchange/CashRecordsTable";
import { CashRecordDetailsSidebar } from "../components/cashExchange/CashRecordDetailsSidebar";
import { useCashRecordManagement } from "../hooks/useCashExchange";
import { useExcelExport } from "../hooks/useExcelExport";
import { useAgentManagement } from "../hooks/useAgents";
import { Agent } from "../api/services/agents/type";

export const CashRecords: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");
  const [selectedShift, setSelectedShift] = useState<string>("");
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
  const [selectedExchangeType, setSelectedExchangeType] =
    useState<string>("all");
  const [selectedRecord, setSelectedRecord] = useState<CashInOutRecord | null>(
    null,
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const itemsPerPage = 10;

  const { exportToExcel } = useExcelExport();
  const { agents } = useAgentManagement();

  // Build query parameters for API call
  const getQueryParams = () => {
    const params: {
      skip: number;
      limit: number;
      date?: string;
      fromDate?: string;
      toDate?: string;
      shift?: string;
      paymentType?: string;
      recordType?: "cash_in" | "cash_out" | "all";
    } = {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      recordType:
        selectedExchangeType === "both"
          ? "all"
          : (selectedExchangeType as "cash_in" | "cash_out" | "all"),
    };

    if (selectedDateFrom && selectedDateTo) {
      params.fromDate = selectedDateFrom;
      params.toDate = selectedDateTo;
    } else if (selectedDate) {
      params.date = selectedDate;
    }

    if (selectedShift) {
      params.shift = selectedShift;
    }

    if (selectedPaymentType) {
      params.paymentType = selectedPaymentType;
    }

    return params;
  };

  const {
    cashRecords,
    pagination,
    isLoading,
    isError,
    error,
    exportCashRecords,
    isExporting,
    exportError,
  } = useCashRecordManagement(getQueryParams());

  const processedRecords = useMemo(() => {
    if (!cashRecords) return [];

    return cashRecords.map(
      (record: CashRecord): CashInOutRecord => ({
        id: record.id,
        type: (record.recordType === "cash_in" ? "cash-in" : "cash-out") as
          | "cash-in"
          | "cash-out",
        amount: record.amount,
        date: record.date,
        shift: record.shift,
        paymentType: record.paymentType,
        currency: record.currency,
        foreignExchangeRate: record.foreignExchangeRate,
        agentId: record.agentId,
        customerId: record.customerId,
        customerDetails: record.customerDetails,
        agentDetails: record.agentDetails,
        createdBy: record.createdBy,
        createdAt: record.createdAt,
      }),
    );
  }, [cashRecords]);

  const agentMap = useMemo(() => {
    const map: Record<string, string> = {};

    if (agents?.items && Array.isArray(agents.items)) {
      for (const agent of agents.items as Agent[]) {
        map[agent.id] = agent.name;
      }
    }

    return map;
  }, [agents]);

  // Calculate totals from current page data
  const totals = useMemo(() => {
    return processedRecords.reduce(
      (
        acc: { totalCashIn: number; totalCashOut: number },
        record: CashInOutRecord,
      ) => {
        if (record.type === "cash-in") {
          acc.totalCashIn += record.amount;
        } else {
          acc.totalCashOut += record.amount;
        }
        return acc;
      },
      { totalCashIn: 0, totalCashOut: 0 },
    );
  }, [processedRecords]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedDate,
    selectedDateFrom,
    selectedDateTo,
    selectedShift,
    selectedPaymentType,
    selectedExchangeType,
  ]);

  const totalPages = pagination
    ? Math.ceil(pagination.total / itemsPerPage)
    : 1;

  // Event handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleDateRangeChange = (dateFrom: string, dateTo: string) => {
    setSelectedDateFrom(dateFrom);
    setSelectedDateTo(dateTo);
    setSelectedDate("");
  };

  const handleDateClear = () => {
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
  };

  const handleShiftChange = (shift: string) => {
    setSelectedShift(shift);
  };

  const handleExchangeTypeChange = (type: string) => {
    setSelectedExchangeType(type);
  };

  const handleClearAllFilters = () => {
    setSelectedDate("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
    setSelectedShift("");
    setSelectedPaymentType("");
    setSelectedExchangeType("all");
  };

  const handleRecordSelect = (record: CashInOutRecord) => {
    setSelectedRecord(record);
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    // Keep the record selected for a moment to allow animation
    setTimeout(() => setSelectedRecord(null), 300);
  };

  const handleExportExcel = async () => {
    try {
      toast.loading("Preparing export...", { id: "export-loading" });

      const exportData = await exportCashRecords();

      if (exportData?.items) {
        const data = exportData.items.map((record: CashRecord) => ({
          Date: new Date(record.date).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }),
          Shift: record.shift,
          Type: record.recordType === "cash_in" ? "cash-in" : "cash-out",
          Amount: record.amount,
          ...(record.recordType === "cash_in"
            ? {
                PaymentType: record.paymentType || "",
                Currency: record.currency || "",
                ForeignExchangeRate: record.foreignExchangeRate || "",
                "Agent Name":
                  agentMap[record.agentId || ""] || record.agentId || "Unknown",
              }
            : {}),
        }));

        exportToExcel({
          data,
          filename: "cash_records",
          sheetName: "Cash Records",
        });

        toast.success("Export completed successfully!", {
          id: "export-loading",
        });
      } else {
        toast.error("No data to export", { id: "export-loading" });
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data", { id: "export-loading" });
    }
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>
              Error loading cash records: {error?.message || "Unknown error"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Export Error Display */}
        {exportError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Export Error: {exportError.message}
          </div>
        )}

        {/* Loading State for Export */}
        {isExporting && (
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
            Exporting data...
          </div>
        )}
        {/* Filters */}
        <CashRecordsFilters
          selectedDate={selectedDate}
          selectedDateFrom={selectedDateFrom}
          selectedDateTo={selectedDateTo}
          selectedShift={selectedShift}
          selectedPaymentType={selectedPaymentType}
          selectedExchangeType={selectedExchangeType}
          onDateChange={handleDateChange}
          onDateRangeChange={handleDateRangeChange}
          onDateClear={handleDateClear}
          onShiftChange={handleShiftChange}
          onExchangeTypeChange={handleExchangeTypeChange}
          onClearAll={handleClearAllFilters}
          onExportExcel={handleExportExcel}
        />

        {/* Cash Records Table */}
        <CashRecordsTable
          records={processedRecords}
          isLoading={isLoading}
          isError={isError}
          error={error}
          totals={totals}
          totalCount={pagination?.total || 0}
          selectedExchangeType={selectedExchangeType}
          onRecordSelect={handleRecordSelect}
          selectedRecordId={selectedRecord?.id}
        />

        {/* Pagination - Server-side pagination */}
        {(pagination?.total || 0) > itemsPerPage && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Results info */}
        {pagination && (
          <div className="mt-4 text-sm text-slate-400 text-center">
            Showing {pagination.returned} of {pagination.total} records
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
        )}
      </div>

      {/* Cash Record Details Sidebar */}
      <CashRecordDetailsSidebar
        record={selectedRecord}
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
      />
    </div>
  );
};
